import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const CART_KEY = "spark_cart_v1";
const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; }
    catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  }, [items]);

  const add = useCallback((product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((p) => p.id === product.id);
      if (existing) {
        return prev.map((p) => (p.id === product.id ? { ...p, quantity: p.quantity + qty } : p));
      }
      return [...prev, {
        id: product.id, sku: product.sku, name: product.name, slug: product.slug,
        price: product.price, shipping_fee: product.shipping_fee || 0,
        image: product.images?.[0]?.storage_path || null, quantity: qty,
      }];
    });
  }, []);

  const setQty = useCallback((id, quantity) => {
    setItems((prev) => prev.map((p) => (p.id === id ? { ...p, quantity: Math.max(1, quantity) } : p)));
  }, []);

  const remove = useCallback((id) => setItems((prev) => prev.filter((p) => p.id !== id)), []);
  const clear = useCallback(() => setItems([]), []);

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, p) => s + p.price * p.quantity, 0);
    const shippingTotal = items.reduce((s, p) => s + (p.shipping_fee || 0) * p.quantity, 0);
    const count = items.reduce((s, p) => s + p.quantity, 0);
    return { subtotal, shippingTotal, count };
  }, [items]);

  return (
    <CartContext.Provider value={{ items, add, setQty, remove, clear, ...totals }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart outside CartProvider");
  return ctx;
};
